import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  Globe,
  Eye,
  EyeOff,
  ExternalLink,
  Loader2,
  LayoutTemplate
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper for authenticated fetch
const authFetch = (url, options = {}) => {
  const token = localStorage.getItem('session_token');
  const headers = { ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(url, { ...options, credentials: 'include', headers });
};

export const BrandSitesPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newSite, setNewSite] = useState({ name: '', slug: '' });

  useEffect(() => {
    const checkAuthAndFetch = async () => {
      try {
        const authRes = await authFetch(`${API_URL}/api/auth/me`);
        if (!authRes.ok || (await authRes.json()).role !== 'admin') {
          navigate('/cms');
          return;
        }

        const sitesRes = await authFetch(`${API_URL}/api/brand-sites`);
        if (sitesRes.ok) {
          setSites(await sitesRes.json());
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuthAndFetch();
  }, [navigate]);

  const handleCreateSite = async () => {
    if (!newSite.name || !newSite.slug) return;

    setCreating(true);
    try {
      const response = await authFetch(`${API_URL}/api/brand-sites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newSite.name,
          slug: newSite.slug.toLowerCase().replace(/[^a-z0-9-]/g, ''),
          status: 'draft'
        })
      });

      if (response.ok) {
        const site = await response.json();
        navigate(`/cms/brand-sites/${site.site_id}`);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create site');
      }
    } catch (error) {
      console.error('Error creating site:', error);
      alert('Error creating site');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteSite = async (siteId, siteName) => {
    if (!window.confirm(`Delete "${siteName}"? This cannot be undone.`)) return;

    try {
      const response = await authFetch(`${API_URL}/api/brand-sites/${siteId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSites(sites.filter(s => s.site_id !== siteId));
      }
    } catch (error) {
      console.error('Error deleting site:', error);
    }
  };

  const handleToggleStatus = async (site) => {
    const newStatus = site.status === 'published' ? 'draft' : 'published';
    
    try {
      const response = await authFetch(`${API_URL}/api/brand-sites/${site.site_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        setSites(sites.map(s => 
          s.site_id === site.site_id ? { ...s, status: newStatus } : s
        ));
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="editorial-container py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/cms" className="text-xl editorial-serif">Cultural Revolution</Link>
            <span className="text-xs uppercase tracking-widest text-muted-foreground px-2 py-1 bg-muted">
              Brand Sites
            </span>
          </div>
          <Link to="/cms">
            <Button variant="ghost" size="sm">
              <ArrowLeft size={16} className="mr-2" />
              Back to CMS
            </Button>
          </Link>
        </div>
      </header>

      <div className="editorial-container py-8">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl editorial-serif mb-2">Brand Sites</h1>
              <p className="text-muted-foreground">
                Create and manage standalone pages for artists, projects, and brands
              </p>
            </div>
            <Button onClick={() => setShowCreateModal(true)} className="gap-2">
              <Plus size={16} />
              New Brand Site
            </Button>
          </div>

          {/* Sites Grid */}
          {sites.length === 0 ? (
            <Card className="p-12 text-center">
              <LayoutTemplate className="mx-auto mb-4 text-muted-foreground" size={48} />
              <h3 className="text-xl font-medium mb-2">No Brand Sites Yet</h3>
              <p className="text-muted-foreground mb-6">
                Create your first brand site for an artist, project, or company
              </p>
              <Button onClick={() => setShowCreateModal(true)} className="gap-2">
                <Plus size={16} />
                Create Your First Site
              </Button>
            </Card>
          ) : (
            <div className="grid gap-4">
              {sites.map((site) => (
                <Card key={site.site_id} className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Site Preview */}
                    <div 
                      className="w-20 h-20 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ 
                        backgroundColor: site.accent_color || '#C41E3A',
                        opacity: site.status === 'draft' ? 0.5 : 1
                      }}
                    >
                      <Globe className="text-white" size={32} />
                    </div>

                    {/* Site Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-medium truncate">{site.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          site.status === 'published' 
                            ? 'bg-green-500/20 text-green-500' 
                            : 'bg-yellow-500/20 text-yellow-500'
                        }`}>
                          {site.status}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-1">
                        /{site.slug}
                      </p>
                      {site.tagline && (
                        <p className="text-sm text-muted-foreground truncate">
                          {site.tagline}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <a
                        href={`/${site.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-muted rounded transition-colors"
                        title="Preview site"
                      >
                        <ExternalLink size={18} className="text-muted-foreground" />
                      </a>
                      <button
                        onClick={() => handleToggleStatus(site)}
                        className="p-2 hover:bg-muted rounded transition-colors"
                        title={site.status === 'published' ? 'Unpublish' : 'Publish'}
                      >
                        {site.status === 'published' ? (
                          <Eye size={18} className="text-green-500" />
                        ) : (
                          <EyeOff size={18} className="text-muted-foreground" />
                        )}
                      </button>
                      <Link
                        to={`/cms/brand-sites/${site.site_id}`}
                        className="p-2 hover:bg-muted rounded transition-colors"
                        title="Edit site"
                      >
                        <Edit size={18} className="text-muted-foreground" />
                      </Link>
                      <button
                        onClick={() => handleDeleteSite(site.site_id, site.name)}
                        className="p-2 hover:bg-muted rounded transition-colors text-red-500"
                        title="Delete site"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Info Box */}
          <Card className="mt-8 p-6 bg-blue-500/10 border-blue-500/20">
            <h3 className="font-medium mb-2 text-blue-400">How Brand Sites Work</h3>
            <ul className="text-sm text-blue-400/80 space-y-1 list-disc list-inside">
              <li>Each brand site gets its own URL (e.g., /selina, /bethetrain)</li>
              <li>Customize sections: Hero, About, Projects, Press, Gallery, Video, Contact, and more</li>
              <li>Choose from font packages and accent colors to match the brand</li>
              <li>Sites can be linked to existing Artists, Projects, or Companies</li>
              <li>Published sites appear in the Cultural Revolution ecosystem</li>
            </ul>
          </Card>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md p-6">
            <h2 className="text-xl font-medium mb-4">Create New Brand Site</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Site Name</label>
                <input
                  type="text"
                  value={newSite.name}
                  onChange={(e) => setNewSite({ 
                    ...newSite, 
                    name: e.target.value,
                    slug: newSite.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-')
                  })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                  placeholder="e.g., Selina Ringel"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">URL Slug</label>
                <div className="flex items-center">
                  <span className="text-muted-foreground mr-1">/</span>
                  <input
                    type="text"
                    value={newSite.slug}
                    onChange={(e) => setNewSite({ 
                      ...newSite, 
                      slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') 
                    })}
                    className="flex-1 px-3 py-2 bg-background border border-border rounded"
                    placeholder="selina-ringel"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  This will be the URL: culturalrevolution.io/{newSite.slug || 'your-slug'}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateSite} 
                disabled={creating || !newSite.name || !newSite.slug}
              >
                {creating ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Site'
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BrandSitesPage;
